import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert,
  Tooltip, IconButton, Divider, Chip,
} from '@mui/material';
import { Link as LinkIcon, LinkOff as LinkOffIcon, Info as InfoIcon } from '@mui/icons-material';
import { Shield } from 'react-feather';
import { API_BASE_URL } from '../../../config/api';
import useIsMobile from '../useIsMobile';
import './AssistantSettingsTheme.css';

// ─── Constants ────────────────────────────────────────────────────────────────
const TYPE_META = {
  anonymize:       { label: 'PII Anonymizer', dir: 'Input' },
  ban_competitors: { label: 'Ban Competitors', dir: 'Input / Output' },
  regex:           { label: 'Regex Filter',   dir: 'Input' },
};

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ─── Single guardrail row ─────────────────────────────────────────────────────
function GuardrailRow({ guardrail, linked, onToggle, actionLoading, isMobile }) {
  const meta = TYPE_META[guardrail.guardrail_type] || { label: guardrail.guardrail_type, dir: '' };
  const cfg = guardrail.config || {};

  const subtitle = (() => {
    if (guardrail.guardrail_type === 'ban_competitors') {
      const names = (cfg.competitors || []).join(', ');
      return names ? `Blocks: ${names}` : 'No competitors configured';
    }
    if (guardrail.guardrail_type === 'regex') {
      const count = (cfg.patterns || []).length;
      return count ? `${count} pattern${count > 1 ? 's' : ''}` : 'No patterns configured';
    }
    return 'Detects PII automatically';
  })();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 1 : 1.5,
        py: 1.25,
        px: isMobile ? 1 : 1.5,
        border: '1px solid',
        borderColor: linked ? 'rgba(23, 157, 166,0.25)' : '#d9d9d9',
        borderRadius: '12px',
        background: linked ? 'rgba(23, 157, 166,0.03)' : '#fff',
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 34, height: 34, borderRadius: '10px', flexShrink: 0,
          background: linked ? 'rgba(23, 157, 166,0.1)' : '#f8f9fc',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: linked ? '#179da6' : '#92959a',
        }}
      >
        <Shield size={16} />
      </Box>

      {/* Text */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: '#373737', lineHeight: 1.2 }}>
          {guardrail.name}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: '#92959a', mt: '2px' }}>
          {meta.label}&nbsp;·&nbsp;{subtitle}
        </Typography>
      </Box>

      {/* Direction chip, the label already carries this on mobile, so it is dropped there */}
      {!isMobile && (
        <Chip
          size="small"
          label={meta.dir}
          sx={{
            fontSize: '0.68rem', fontWeight: 800, height: 20,
            background: linked ? 'rgba(23, 157, 166,0.1)' : '#f1f3f9',
            color: linked ? '#179da6' : '#6f7276',
            border: '1px solid', borderColor: linked ? 'rgba(23, 157, 166,0.2)' : '#d9d9d9',
            borderRadius: '999px',
          }}
        />
      )}

      {/* Action */}
      <Tooltip title={linked ? 'Unlink guardrail' : 'Link guardrail'}>
        <span>
          <IconButton
            size="small"
            onClick={() => onToggle(guardrail, !linked)}
            disabled={actionLoading}
            className="assistantIconButtonNeutral"
            sx={{
              border: '1px solid',
              borderColor: linked ? 'rgba(231,74,59,0.25)' : 'rgba(23, 157, 166,0.25)',
              background: linked ? 'rgba(231,74,59,0.05)' : 'rgba(23, 157, 166,0.05)',
              '&:hover': {
                background: linked ? 'rgba(231,74,59,0.12)' : 'rgba(23, 157, 166,0.12)',
              },
            }}
          >
            {actionLoading
              ? <CircularProgress size={16} />
              : linked
                ? <LinkOffIcon sx={{ fontSize: 16, color: '#e74a3b' }} />
                : <LinkIcon    sx={{ fontSize: 16, color: '#179da6' }} />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const GuardrailsSettings = ({ assistant, onUpdate }) => {
  const isMobile                        = useIsMobile();
  const [loading, setLoading]           = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [allGuardrails, setAllGuardrails] = useState([]);
  const [linkedIds, setLinkedIds]       = useState(new Set());
  const [error, setError]               = useState(null);
  const [success, setSuccess]           = useState(null);

  // Fetch the full library + which ones are linked to this agent
  useEffect(() => {
    const load = async () => {
      if (!assistant?.id) return;
      try {
        setLoading(true);
        setError(null);
        const headers = { 'Content-Type': 'application/json', ...getAuthHeader() };

        const [libRes, agentRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/guardrails`, { headers }),
          fetch(`${API_BASE_URL}/api/guardrails?agent_id=${assistant.id}`, { headers }),
        ]);

        if (!libRes.ok)   throw new Error('Failed to load guardrail library');
        if (!agentRes.ok) throw new Error('Failed to load agent guardrails');

        const [lib, linked] = await Promise.all([libRes.json(), agentRes.json()]);
        setAllGuardrails(lib);
        setLinkedIds(new Set(linked.map((g) => g.guardrail_id ?? g.id)));
      } catch (err) {
        setError(err.message || 'Failed to load guardrails.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [assistant]);

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleToggle = async (guardrail, shouldLink) => {
    try {
      setActionLoading(true);
      setError(null);
      const gid = guardrail.id ?? guardrail.guardrail_id;
      const url = shouldLink
        ? `${API_BASE_URL}/api/guardrails/${assistant.id}/link`
        : `${API_BASE_URL}/api/guardrails/${assistant.id}/unlink`;
      const method = shouldLink ? 'POST' : 'DELETE';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ guardrail_id: gid }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to update guardrail link');
      }

      setLinkedIds((prev) => {
        const next = new Set(prev);
        shouldLink ? next.add(gid) : next.delete(gid);
        return next;
      });

      showSuccess(shouldLink ? 'Guardrail linked' : 'Guardrail unlinked');
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.message || 'An error occurred.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const linked   = allGuardrails.filter((g) => linkedIds.has(g.id));
  const unlinked = allGuardrails.filter((g) => !linkedIds.has(g.id));

  return (
    <Box className="assistantSettingsRoot">
      {error   && <Alert severity="error"   onClose={() => setError(null)}   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      {/* Linked guardrails */}
      <Paper elevation={0} className="assistantSettingsPanel">
        <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 900, color: '#373737' }}>
          Active Guardrails
        </Typography>
        {!isMobile && (
          <Typography variant="body2" sx={{ color: '#6f7276', mb: 2, lineHeight: 1.55 }}>
            These guardrails are enforced on every message sent to or from this agent.
          </Typography>
        )}

        {linked.length === 0 ? (
          <Typography color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.88rem' }}>
            {isMobile ? 'No guardrails linked.' : 'No guardrails linked. Add one below to start protecting your agent.'}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {linked.map((g, i) => (
              <React.Fragment key={g.id}>
                <GuardrailRow
                  guardrail={g}
                  linked={true}
                  onToggle={handleToggle}
                  actionLoading={actionLoading}
                  isMobile={isMobile}
                />
                {i < linked.length - 1 && <Divider sx={{ borderColor: '#f1f3f9' }} />}
              </React.Fragment>
            ))}
          </Box>
        )}
      </Paper>

      {/* Available guardrails */}
      <Paper elevation={0} className="assistantSettingsPanel">
        <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 900, color: '#373737' }}>
          Available Guardrails
        </Typography>
        {!isMobile && (
          <Typography variant="body2" sx={{ color: '#6f7276', mb: 2, lineHeight: 1.55 }}>
            Guardrails from your library. Link them to this agent to activate enforcement.
          </Typography>
        )}

        {unlinked.length === 0 && allGuardrails.length === 0 ? (
          <Typography color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.88rem' }}>
            No guardrails in your library yet. Create some on the&nbsp;
            <a href="/guardrails" style={{ color: '#179da6', fontWeight: 700 }}>Guardrails page</a>.
          </Typography>
        ) : unlinked.length === 0 ? (
          <Typography color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.88rem' }}>
            All your guardrails are already linked to this agent.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {unlinked.map((g, i) => (
              <React.Fragment key={g.id}>
                <GuardrailRow
                  guardrail={g}
                  linked={false}
                  onToggle={handleToggle}
                  actionLoading={actionLoading}
                  isMobile={isMobile}
                />
                {i < unlinked.length - 1 && <Divider sx={{ borderColor: '#f1f3f9' }} />}
              </React.Fragment>
            ))}
          </Box>
        )}
      </Paper>

      <Box className="assistantHintRow" sx={{ mt: 1 }}>
        <InfoIcon color="info" fontSize="small" />
        <Typography variant="body2" color="text.secondary">
          Input guardrails run before the LLM sees a message. Output guardrails run before the response reaches the user.
          Manage your guardrail library on the <a href="/guardrails" style={{ color: '#179da6', fontWeight: 700 }}>Guardrails page</a>.
        </Typography>
      </Box>
    </Box>
  );
};

export default GuardrailsSettings;
